import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTemplate } from '../../context/TemplateContext';
import ColorPicker from './ColorPicker';
import PropertyField from './PropertyField';

const ElementProperties = ({ element }) => {
  const { updateElement } = useTemplate();
  const [properties, setProperties] = useState({});

  useEffect(() => {
    // Initialize properties from element
    setProperties({
      content: element.content || '',
      src: element.src || element.defaultSrc || '',
      alt: element.alt || element.defaultAlt || '',
      href: element.href || element.defaultHref || '#',
      width: element.width || '100%',
      height: element.height || '20px',
      includeUnsubscribe: element.includeUnsubscribe || false,
      styles: {
        ...(element.styles || {}),
      }
    });
  }, [element]);

  const handleChange = (key, value) => {
    setProperties(prev => ({
      ...prev,
      [key]: value
    }));
    updateElement(element.id, { [key]: value });
  };

  const handleStyleChange = (key, value) => {
    const newStyles = { ...properties.styles, [key]: value };
    setProperties(prev => ({
      ...prev,
      styles: newStyles
    }));
    updateElement(element.id, { styles: newStyles });
  };

  const renderCommonProperties = () => (
    <>
      {(element.type === 'header' || element.type === 'text' || element.type === 'button' || element.type === 'footer') && (
        <PropertyField
          label="Content"
          type="textarea"
          value={properties.content || ''}
          onChange={(value) => handleChange('content', value)}
        />
      )}
      
      <PropertyGroup>
        <PropertyGroupTitle>Appearance</PropertyGroupTitle>
        
        {(element.type === 'header' || element.type === 'text' || element.type === 'button' || element.type === 'footer') && (
          <>
            <PropertyField
              label="Font Size"
              type="select"
              value={properties.styles?.fontSize || '16px'}
              options={[
                { value: '12px', label: '12px' },
                { value: '14px', label: '14px' },
                { value: '16px', label: '16px' },
                { value: '18px', label: '18px' },
                { value: '20px', label: '20px' },
                { value: '24px', label: '24px' },
                { value: '28px', label: '28px' },
                { value: '32px', label: '32px' },
                { value: '36px', label: '36px' },
                { value: '48px', label: '48px' },
              ]}
              onChange={(value) => handleStyleChange('fontSize', value)}
            />
            
            <PropertyField
              label="Text Align"
              type="select"
              value={properties.styles?.textAlign || 'left'}
              options={[
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
              ]}
              onChange={(value) => handleStyleChange('textAlign', value)}
            />
            
            <PropertyField
              label="Text Color"
              type="color"
              value={properties.styles?.color || '#333333'}
              onChange={(value) => handleStyleChange('color', value)}
            />
          </>
        )}
        
        <PropertyField
          label="Background Color"
          type="color"
          value={properties.styles?.backgroundColor || 'transparent'}
          onChange={(value) => handleStyleChange('backgroundColor', value)}
        />
        
        <PropertyField
          label="Padding"
          type="select"
          value={properties.styles?.padding || '10px'}
          options={[
            { value: '0px', label: 'None' },
            { value: '5px', label: '5px' },
            { value: '10px', label: '10px' },
            { value: '15px', label: '15px' },
            { value: '20px', label: '20px' },
            { value: '30px', label: '30px' },
            { value: '10px 20px', label: '10px 20px' },
            { value: '20px 40px', label: '20px 40px' },
          ]}
          onChange={(value) => handleStyleChange('padding', value)}
        />
      </PropertyGroup>
    </>
  );

  const renderSpecificProperties = () => {
    switch (element.type) {
      case 'image':
        return (
          <PropertyGroup>
            <PropertyGroupTitle>Image Properties</PropertyGroupTitle>
            <PropertyField
              label="Image URL"
              type="text"
              value={properties.src || ''}
              onChange={(value) => handleChange('src', value)}
            />
            <PropertyField
              label="Alt Text"
              type="text"
              value={properties.alt || ''}
              onChange={(value) => handleChange('alt', value)}
            />
            <PropertyField
              label="Width"
              type="text"
              value={properties.width || '100%'}
              onChange={(value) => handleChange('width', value)}
            />
          </PropertyGroup>
        );
      case 'button':
        return (
          <PropertyGroup>
            <PropertyGroupTitle>Button Properties</PropertyGroupTitle>
            <PropertyField
              label="Link URL"
              type="text"
              value={properties.href || '#'}
              onChange={(value) => handleChange('href', value)}
            />
            <PropertyField
              label="Button Color"
              type="color"
              value={properties.styles?.buttonColor || '#5876ff'}
              onChange={(value) => handleStyleChange('buttonColor', value)}
            />
            <PropertyField
              label="Text Color"
              type="color"
              value={properties.styles?.textColor || '#ffffff'}
              onChange={(value) => handleStyleChange('textColor', value)}
            />
            <PropertyField
              label="Border Radius"
              type="select"
              value={properties.styles?.borderRadius || '4px'}
              options={[
                { value: '0px', label: 'None' },
                { value: '2px', label: '2px' },
                { value: '4px', label: '4px' },
                { value: '8px', label: '8px' },
                { value: '16px', label: '16px' },
                { value: '24px', label: '24px' },
              ]}
              onChange={(value) => handleStyleChange('borderRadius', value)}
            />
          </PropertyGroup>
        );
      case 'spacer':
        return (
          <PropertyGroup>
            <PropertyGroupTitle>Spacer Properties</PropertyGroupTitle>
            <PropertyField
              label="Height"
              type="select"
              value={properties.height || '20px'}
              options={[
                { value: '10px', label: '10px' },
                { value: '20px', label: '20px' },
                { value: '30px', label: '30px' },
                { value: '40px', label: '40px' },
                { value: '50px', label: '50px' },
              ]}
              onChange={(value) => handleChange('height', value)}
            />
          </PropertyGroup>
        );
      case 'divider':
        return (
          <PropertyGroup>
            <PropertyGroupTitle>Divider Properties</PropertyGroupTitle>
            <PropertyField
              label="Color"
              type="color"
              value={properties.styles?.color || '#dddddd'}
              onChange={(value) => handleStyleChange('color', value)}
            />
          </PropertyGroup>
        );
      case 'footer':
        return (
          <PropertyGroup>
            <PropertyGroupTitle>Footer Properties</PropertyGroupTitle>
            <PropertyField
              label="Include Unsubscribe Link"
              type="checkbox"
              checked={properties.includeUnsubscribe || false}
              onChange={(value) => handleChange('includeUnsubscribe', value)}
            />
          </PropertyGroup>
        );
      default:
        return null;
    }
  };

  return (
    <PropertiesContainer>
      <ElementTypeLabel>{element.type}</ElementTypeLabel>
      {renderCommonProperties()}
      {renderSpecificProperties()}
    </PropertiesContainer>
  );
};

const PropertiesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ElementTypeLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #5876ff;
  text-transform: capitalize;
  margin-bottom: 10px;
`;

const PropertyGroup = styled.div`
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 15px;
  margin-bottom: 15px;
`;

const PropertyGroupTitle = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: #333;
  margin: 0 0 15px 0;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
`;

export default ElementProperties;
